// src/pages/StaffInvite.tsx
import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { companyRoute } from "../utils/routeHelpers";
import { apiGet, apiPost } from "../api/client";
import { Button } from "../components/Button";
import { formatAnyDateToLocale } from "../utils/dateUtils";
import { A, pillClass } from "../styles/appleClasses";

type Status = "idle" | "submitting" | "success" | "error";

interface StaffUser {
  id: number;
  email: string;
  full_name: string | null;
  is_company_admin: boolean;
  is_active: boolean;
  created_at: string;
}

export default function StaffInvite() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id: string }>();

  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<{ raw: string } | { fallback: "invite" } | null>(null);

  const [staff, setStaff] = useState<StaffUser[]>([]);
  const [loadingStaff, setLoadingStaff] = useState<boolean>(true);
  const [rowActionId, setRowActionId] = useState<number | null>(null);

  const labelCls = `block mb-1 ${A.label}`;
  const inputBase = A.input;

  const pillBtnBase =
    "inline-flex items-center rounded-full px-3 py-1.5 text-xs font-medium leading-none ring-1 " +
    "focus:outline-none focus:ring-2 focus:ring-appleblueD transition";

  const pillEnable = `${pillBtnBase} bg-appleblue/10 text-appleblue ring-appleblue/20 hover:bg-appleblue/20`;
  const pillDisable = `${pillBtnBase} bg-red-50 text-red-700 ring-red-200 hover:bg-red-100`;

  const formatJoined = (iso: string | null | undefined) => {
    if (!iso) return "—";
    try {
      return formatAnyDateToLocale(iso);
    } catch {
      return "—";
    }
  };

  // --- Load staff list ---
  const loadStaff = async () => {
    setLoadingStaff(true);
    try {
      const data = await apiGet<StaffUser[]>("/auth/staff");
      setStaff(data || []);
    } catch (err) {
      console.error("Failed to load staff", err);
      setStaff([]);
    } finally {
      setLoadingStaff(false);
    }
  };

  useEffect(() => {
    loadStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Invite form submit ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setStatus("submitting");
    setError(null);

    try {
      await apiPost("/auth/invite-staff", { email: email.trim().toLowerCase() });
      setStatus("success");
      setEmail("");
      await loadStaff();
    } catch (err: any) {
      console.error(err);
      setStatus("error");
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.join(", ") : detail;
      setError(msg ? { raw: msg } : { fallback: "invite" });
    }
  };

  // --- Disable / enable handlers ---
  const handleDisable = async (userId: number) => {
    if (!window.confirm(i18n.t("staffInvite.confirmDisable")))
      return;

    setRowActionId(userId);
    try {
      await apiPost(`/auth/staff/${userId}/disable`, {});
      await loadStaff();
    } catch (err: any) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.join(", ") : detail;
      alert(msg || i18n.t("staffInvite.errors.disable"));
    } finally {
      setRowActionId(null);
    }
  };

  const handleEnable = async (userId: number) => {
    setRowActionId(userId);
    try {
      await apiPost(`/auth/staff/${userId}/enable`, {});
      await loadStaff();
    } catch (err: any) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.join(", ") : detail;
      alert(msg || i18n.t("staffInvite.errors.enable"));
    } finally {
      setRowActionId(null);
    }
  };

  const closeTo = companyRoute(company_id, "/settings");
  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center rounded-full ` +
    "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD";

  return (
    <div
      className={`min-h-screen bg-gray-50 ${A.body} flex flex-col items-center pt-4 sm:pt-6`}
    >
      {/* Header: Back | Title | X */}
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-2xl flex items-center border-b border-gray-100 px-2 pb-2 mb-4">
        <button type="button" onClick={() => navigate(-1)} className={iconBtn} aria-label={t("directory.common.back")}>
          <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>

        <h1 className={`flex-1 text-center ${A.title}`}>{t("staffInvite.title")}</h1>

        <button type="button" onClick={() => navigate(closeTo)} className={iconBtn} aria-label={t("directory.common.close")}>
          <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>
      </div>

      <div className="w-full max-w-2xl px-2 space-y-4">
        {/* Invite card */}
        <div className={`${A.card} overflow-visible`}>
          <div className="p-4 sm:p-6 border-b border-gray-100">
            <div className={A.title}>{t("staffInvite.inviteTitle")}</div>
            <div className={`mt-1 ${A.label}`}>
              {t("staffInvite.description")}
            </div>
          </div>

          <div className="p-4 sm:p-6">
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className={labelCls}>{t("staffInvite.email")}</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputBase}
                  placeholder="staff@example.com"
                  autoComplete="email"
                />
              </div>

              {/* reserve space to avoid layout jump */}
              <div style={{ minHeight: 22 }}>
                {error && <div className="text-[13px] text-red-600">{"raw" in error ? error.raw : t("staffInvite.errors.invite")}</div>}
                {status === "success" && (
                  <div className="text-[13px] text-green-600">
                    {t("staffInvite.sent")}
                  </div>
                )}
              </div>

              <Button
                type="submit"
                variant="primary"
                className="w-full justify-center"
                disabled={status === "submitting"}
              >
                {status === "submitting" ? t("sales.header.sending") : t("staffInvite.send")}
              </Button>
            </form>
          </div>
        </div>

        {/* Staff list */}
        <div className={`${A.card} overflow-visible`}>
          <div className="p-4 sm:p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className={A.title}>{t("staffInvite.currentUsers")}</div>
              {loadingStaff && <span className="text-xs text-gray-400">{t("directory.common.loading")}</span>}
            </div>
          </div>

          <div className="p-4 sm:p-6">
            {staff.length === 0 && !loadingStaff ? (
              <div className={A.label}>{t("staffInvite.empty")}</div>
            ) : (
              <>
                {/* Mobile: cards */}
                <div className="space-y-3 sm:hidden">
                  {staff.map((u) => {
                    const isOwner = u.is_company_admin;
                    const disabled = !u.is_active;
                    const busy = rowActionId === u.id;

                    return (
                      <div
                        key={u.id}
                        className={`${A.card} p-3 ${disabled ? "opacity-60" : ""}`}
                      >
                        <div className="flex justify-between items-start mb-1.5">
                          <div className="min-w-0">
                            <div className="text-[13px] font-semibold text-gray-700">
                              {u.full_name || "—"}
                            </div>
                            <div className={`${A.label} break-all`}>{u.email}</div>
                          </div>

                          <span className={`${pillClass(disabled ? "neutral" : "success")} text-[11px]`}>
                            {disabled ? t("staffInvite.disabled") : t("staffInvite.active")}
                          </span>
                        </div>

                        <div className="flex justify-between items-center mt-2">
                          <div className="flex flex-col text-[11px] text-gray-500">
                            <span>{isOwner ? t("staffInvite.ownerAdmin") : t("staffInvite.staffPos")}</span>
                            <span className="mt-0.5">{t("staffInvite.joinedDate", { date: formatJoined(u.created_at) })}</span>
                          </div>

                          {isOwner ? (
                            <span className="text-[11px] text-gray-400">{t("staffInvite.owner")}</span>
                          ) : disabled ? (
                            <Button
                              type="button"
                              variant="secondary"
                              minWidth="0px"
                              className={pillEnable}
                              onClick={() => handleEnable(u.id)}
                              disabled={busy}
                            >
                              {busy ? t("staffInvite.reenabling") : t("staffInvite.reenable")}
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              variant="secondary"
                              minWidth="0px"
                              className={pillDisable}
                              onClick={() => handleDisable(u.id)}
                              disabled={busy}
                            >
                              {busy ? t("staffInvite.disabling") : t("staffInvite.disable")}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Desktop/tablet: table */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-[13px]">
                    <thead className="bg-gray-50/60">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                          {t("register.name")}
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                          {t("directory.common.email")}
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                          {t("staffInvite.role")}
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                          {t("sales.common.status")}
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                          {t("staffInvite.joined")}
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">
                          {t("staffInvite.actions")}
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-100">
                      {staff.map((u) => {
                        const isOwner = u.is_company_admin;
                        const disabled = !u.is_active;
                        const busy = rowActionId === u.id;

                        return (
                          <tr key={u.id} className={disabled ? "opacity-60" : ""}>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                              {u.full_name || "—"}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                              {u.email}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                              {isOwner ? t("staffInvite.ownerAdmin") : t("staffInvite.staffPos")}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <span className={pillClass(disabled ? "neutral" : "success")}>
                                {disabled ? t("staffInvite.disabled") : t("staffInvite.active")}
                              </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                              {formatJoined(u.created_at)}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-right">
                              {isOwner ? (
                                <span className="text-xs text-gray-400">—</span>
                              ) : disabled ? (
                                <Button
                                  type="button"
                                  variant="secondary"
                                  minWidth="0px"
                                  className={pillEnable}
                                  onClick={() => handleEnable(u.id)}
                                  disabled={busy}
                                >
                                  {busy ? t("staffInvite.reenabling") : t("staffInvite.reenable")}
                                </Button>
                              ) : (
                                <Button
                                  type="button"
                                  variant="secondary"
                                  minWidth="0px"
                                  className={pillDisable}
                                  onClick={() => handleDisable(u.id)}
                                  disabled={busy}
                                >
                                  {busy ? t("staffInvite.disabling") : t("staffInvite.disable")}
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
